/**
 * DrawerManager - Manages all drawers in the theme
 * Handles:
 * - Cart drawer
 * - Delivery location drawer
 * - Quick view product drawers
 * - Facet / filter drawer
 *
 * Ensures proper tracking of drawer states and hides the chat widget
 * when any drawer is open.
 */
class DrawerManager {
  constructor() {
    this.observers = [];
    this.drawerStates = {
      cart: false,
      delivery: false,
      quickView: false,
      menu: false,
      facets: false,
    };

    this.init();
  }

  init() {
    document.addEventListener("DOMContentLoaded", () => {
      this.setupObservers();
      this.setupEventListeners();
    });
  }

  setupObservers() {
    const attributeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "open" ||
            mutation.attributeName === "active")
        ) {
          this.updateDrawerOpenState();
        }
      });
    });

    this.observers.push(attributeObserver);

    const cartDrawer = document.getElementById("CartDrawer");
    if (cartDrawer) {
      attributeObserver.observe(cartDrawer, { attributes: true });
    }

    const deliveryDrawer = document.getElementById("DeliveryLocationDrawer");
    if (deliveryDrawer) {
      attributeObserver.observe(deliveryDrawer, { attributes: true });
    }

    const menuDrawer = document.getElementById("MenuDrawer");
    if (menuDrawer) {
      attributeObserver.observe(menuDrawer, { attributes: true });
    }

    const facetDrawer = document.getElementById("FacetDrawer");
    if (facetDrawer) {
      attributeObserver.observe(facetDrawer, { attributes: true });
    }

    document.querySelectorAll(".quick-view").forEach((quickView) => {
      attributeObserver.observe(quickView, { attributes: true });
    });

    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const quickViews =
                node.classList && node.classList.contains("quick-view")
                  ? [node]
                  : node.querySelectorAll(".quick-view");

              quickViews.forEach((quickView) => {
                attributeObserver.observe(quickView, { attributes: true });
              });

              if (
                node.id === "DeliveryLocationDrawer" ||
                node.querySelector("#DeliveryLocationDrawer")
              ) {
                const deliveryDrawer =
                  node.id === "DeliveryLocationDrawer"
                    ? node
                    : node.querySelector("#DeliveryLocationDrawer");

                if (deliveryDrawer) {
                  attributeObserver.observe(deliveryDrawer, {
                    attributes: true,
                  });
                }
              }

              if (
                node.id === "MenuDrawer" ||
                node.querySelector("#MenuDrawer")
              ) {
                const menuDrawer =
                  node.id === "MenuDrawer"
                    ? node
                    : node.querySelector("#MenuDrawer");

                if (menuDrawer) {
                  attributeObserver.observe(menuDrawer, {
                    attributes: true,
                  });
                }
              }

              if (
                node.id === "FacetDrawer" ||
                node.querySelector("#FacetDrawer")
              ) {
                const observedFacetDrawer =
                  node.id === "FacetDrawer"
                    ? node
                    : node.querySelector("#FacetDrawer");

                if (observedFacetDrawer) {
                  attributeObserver.observe(observedFacetDrawer, {
                    attributes: true,
                  });
                }
              }
            }
          });
        }
      });
    });

    this.observers.push(bodyObserver);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  setupEventListeners() {
    document.addEventListener("cart:opened", () => {
      this.drawerStates.cart = true;
      this.updateBodyClass();
    });

    document.addEventListener("cart:closed", () => {
      this.drawerStates.cart = false;
      this.updateDrawerOpenState();
    });

    document.addEventListener("delivery:opened", () => {
      this.drawerStates.delivery = true;
      this.updateBodyClass();
    });

    document.addEventListener("delivery:closed", () => {
      this.drawerStates.delivery = false;
      this.updateDrawerOpenState();
    });

    document.addEventListener("quick-view:opened", () => {
      this.drawerStates.quickView = true;
      this.updateBodyClass();
    });

    document.addEventListener("quick-view:closed", () => {
      this.drawerStates.quickView = false;
      this.updateDrawerOpenState();
    });

    document.addEventListener("menu-drawer:open", () => {
      this.drawerStates.menu = true;
      this.updateBodyClass();
    });

    document.addEventListener("menu-drawer:close", () => {
      this.drawerStates.menu = false;
      this.updateDrawerOpenState();
    });
  }

  updateDrawerOpenState() {
    const cartDrawer = document.getElementById("CartDrawer");
    const deliveryDrawer = document.getElementById("DeliveryLocationDrawer");
    const menuDrawer = document.getElementById("MenuDrawer");
    const facetDrawer = document.getElementById("FacetDrawer");
    const quickViewElements = document.querySelectorAll(".quick-view[open]");

    this.drawerStates.cart = !!(
      cartDrawer &&
      (cartDrawer.hasAttribute("open") || cartDrawer.hasAttribute("active"))
    );
    this.drawerStates.delivery = !!(
      deliveryDrawer &&
      (deliveryDrawer.hasAttribute("open") ||
        deliveryDrawer.hasAttribute("active"))
    );
    this.drawerStates.menu = !!(
      menuDrawer &&
      (menuDrawer.hasAttribute("open") || menuDrawer.hasAttribute("active"))
    );
    this.drawerStates.facets = !!(
      facetDrawer &&
      (facetDrawer.hasAttribute("open") || facetDrawer.hasAttribute("active"))
    );
    this.drawerStates.quickView = quickViewElements.length > 0;

    this.updateBodyClass();
  }

  updateBodyClass() {
    const isAnyDrawerOpen =
      this.drawerStates.cart ||
      this.drawerStates.delivery ||
      this.drawerStates.quickView ||
      this.drawerStates.menu ||
      this.drawerStates.facets;

    if (isAnyDrawerOpen) {
      document.body.classList.add("drawer-open");
    } else {
      document.body.classList.remove("drawer-open");
    }
  }
}

window.drawerManager = new DrawerManager();
